import { isJwtExpiredOrInvalid } from 'utils/jwt';

import {
  getAdminAccessToken,
  getAdminIdToken,
  getUserAccessToken,
  getUserIdToken,
} from './authTokenStorage';
import { refreshSessionTokens } from './refreshSession';
import { getSessionRealm, setSessionRealm, type SessionRealm } from './sessionRealm';
import { terminateSession } from './terminateSession';

export type SessionValidationResult = 'anonymous' | 'valid' | 'refreshed' | 'expired';

const getRealmBearerToken = (realm: SessionRealm): string | null =>
  realm === 'admin'
    ? (getAdminIdToken() ?? getAdminAccessToken())
    : (getUserIdToken() ?? getUserAccessToken());

const inferSessionRealm = (): SessionRealm | null => {
  const storedRealm = getSessionRealm();
  if (storedRealm) {
    return storedRealm;
  }

  const hasAdminToken = Boolean(getRealmBearerToken('admin'));
  const hasUserToken = Boolean(getRealmBearerToken('user'));

  if (hasAdminToken === hasUserToken) {
    if (!hasAdminToken || typeof window === 'undefined') {
      return null;
    }

    return window.location.pathname.startsWith('/admin') ? 'admin' : 'user';
  }

  return hasAdminToken ? 'admin' : 'user';
};

export async function validatePersistedSession(): Promise<SessionValidationResult> {
  const realm = inferSessionRealm();
  if (!realm) {
    return 'anonymous';
  }

  setSessionRealm(realm);
  const bearerToken = getRealmBearerToken(realm);
  if (bearerToken && !isJwtExpiredOrInvalid(bearerToken)) {
    return 'valid';
  }

  const hadInMemorySession = Boolean(bearerToken);

  try {
    const refreshed = await refreshSessionTokens();
    const refreshedBearer = refreshed?.idToken ?? refreshed?.accessToken ?? null;
    if (refreshedBearer && !isJwtExpiredOrInvalid(refreshedBearer)) {
      return 'refreshed';
    }
  } catch {
    // Session cleanup below is shared by invalid and rejected refresh tokens.
  }

  // Tokens live in Redux and a tab sessionStorage mirror. A leftover realm key
  // with no bearer/refresh token is unrestorable, not expired — do not purge
  // euId / journey helpers on every refresh.
  if (!hadInMemorySession) {
    return 'anonymous';
  }

  await terminateSession(realm);
  return 'expired';
}
